import React from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';

/**
 * Skeleton components for loading states
 */

export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 5,
}) => (
  <TableContainer component={Paper}>
    <Table>
      <TableHead>
        <TableRow>
          {Array.from({ length: columns }).map((_, i) => (
            <TableCell key={`header-${i}`}>
              <Skeleton width="80%" />
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: rows }).map((_, rowIdx) => (
          <TableRow key={`row-${rowIdx}`}>
            {Array.from({ length: columns }).map((__, colIdx) => (
              <TableCell key={`cell-${rowIdx}-${colIdx}`}>
                <Skeleton width="70%" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

export const CardSkeleton: React.FC<{ lines?: number }> = ({ lines = 3 }) => (
  <Card sx={{ mb: 2 }}>
    <CardContent>
      <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={`line-${i}`}
          width={i === lines - 1 ? '80%' : '100%'}
          sx={{ mb: 1 }}
        />
      ))}
    </CardContent>
  </Card>
);

export const TextFieldSkeleton: React.FC<{ rows?: number }> = ({
  rows = 1,
}) => (
  <Box sx={{ mb: 2 }}>
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton
        key={`textfield-${i}`}
        variant="rectangular"
        height={56}
        sx={{ mb: 1 }}
      />
    ))}
  </Box>
);

export const GridSkeleton: React.FC<{ columns?: number; items?: number }> = ({
  columns = 3,
  items = 6,
}) => (
  <Grid container spacing={2}>
    {Array.from({ length: items }).map((_, i) => (
      <Grid item xs={12} sm={6} md={12 / columns} key={`grid-item-${i}`}>
        <Skeleton variant="rectangular" height={200} />
      </Grid>
    ))}
  </Grid>
);

export const ListSkeleton: React.FC<{ items?: number }> = ({ items = 5 }) => (
  <Box>
    {Array.from({ length: items }).map((_, i) => (
      <Box key={`list-item-${i}`} sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Skeleton variant="circular" width={40} height={40} />
        <Box sx={{ flex: 1 }}>
          <Skeleton width="60%" />
          <Skeleton width="40%" sx={{ mt: 1 }} />
        </Box>
      </Box>
    ))}
  </Box>
);

export const RequirementTableSkeleton: React.FC = () => (
  <TableSkeleton rows={5} columns={5} />
);

export const AnalysisReviewSkeleton: React.FC = () => (
  <Grid container spacing={2}>
    <Grid item xs={12} md={4}>
      <Paper sx={{ p: 2 }}>
        <Skeleton width="60%" height={24} sx={{ mb: 2 }} />
        <ListSkeleton items={4} />
      </Paper>
    </Grid>
    <Grid item xs={12} md={8}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Skeleton width="40%" height={32} sx={{ mb: 2 }} />
          <Grid container spacing={1}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Grid item xs={6} key={`stat-${i}`}>
                <Skeleton height={40} />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Skeleton width="30%" height={24} sx={{ mb: 2 }} />
          <TableSkeleton rows={3} columns={3} />
        </CardContent>
      </Card>
    </Grid>
  </Grid>
);

export const AnalysisProgressSkeleton: React.FC = () => (
  <Card>
    <CardContent>
      <Skeleton width="50%" height={32} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" height={20} sx={{ mb: 2 }} />
      <TableSkeleton rows={5} columns={4} />
    </CardContent>
  </Card>
);

export const ImportFormSkeleton: React.FC = () => (
  <Card sx={{ mb: 2 }}>
    <CardContent>
      <Skeleton width="40%" height={32} sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Skeleton width={100} height={40} />
        <Skeleton width={100} height={40} />
      </Box>
      <Skeleton variant="rectangular" height={150} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" height={40} />
    </CardContent>
  </Card>
);
